import { Box } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import MarketProviders from 'providers/market/MarketProviders';
import { ReactNode } from 'react';
import { Outlet } from 'react-router';
import { isStaging } from 'config/env';
import { useIsPrint } from 'hooks/useIsPrint';
import useIsViewport from 'hooks/useIsViewport';
import useUser from 'hooks/useUser';
import { isDynamicImportChunkError } from 'utils/dynamicImportError';
import { isHeadless } from 'utils/headless';
import Navigation from './Navigation';
import NetworkError from './NetworkError';
import UpdateVersion from './UpdateVersion';

const Screen = ({ children = null }: { children?: ReactNode | null }) => {
  const user = useUser();
  const isPrint = useIsPrint();
  const isMobile = useIsViewport();

  if (isHeadless()) {
    return (
      <>
        {user ? (
          <>
            <Outlet />
            {children}
          </>
        ) : null}
      </>
    );
  }

  // Printing flips isPrint mid-session, so the two branches below share one MarketProviders
  // instance: the chrome is what comes and goes, the market context the screens read stays put.
  return (
    <MarketProviders>
      {isPrint ? (
        user ? (
          <>
            <Outlet />
            {children}
          </>
        ) : null
      ) : (
        <Navigation>
          {isStaging && isMobile && (
            <Box
              sx={(theme) => ({
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                minHeight: '2px',
                maxHeight: '2px',
                backgroundColor: theme.palette.warning.main,
                zIndex: theme.zIndex.appBar + 1,
              })}
            />
          )}
          <ErrorBoundary
            fallback={({ error, componentStack }) => {
              // Type narrowing for the error object
              const typedError = error instanceof Error ? error : new Error('Unknown error');

              // Auto-reload for failed dynamic imports (common after deploys)
              if (isDynamicImportChunkError(typedError)) {
                // Force reload to get fresh HTML with correct chunk references
                window.location.reload();
                return <></>; // Return empty fragment while reloading
              }

              return (
                <NetworkError
                  message="We have encountered an unexpected error."
                  error={typedError}
                  componentStack={componentStack}
                />
              );
            }}
          >
            <Box
              sx={{
                marginBottom: 'calc(env(safe-area-inset-bottom))',
                overflowX: 'clip',
              }}
            >
              <UpdateVersion />
              <Outlet />
              {children}
            </Box>
          </ErrorBoundary>
        </Navigation>
      )}
    </MarketProviders>
  );
};

export default Screen;
